import { useState } from "react";
import { Link, Navigate, useLocation } from "react-router";
import { useLogin } from "@/hooks/api/useLogin";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const location = useLocation();
  const login = useLogin();

  // Where ProtectedRoute redirected from (e.g. an invite link /join/:code or
  // a device pairing link /activate?code=X — keep the query string)
  const from = (location.state as { from?: { pathname: string; search?: string } })
    ?.from;
  const returnTo = from ? from.pathname + (from.search ?? "") : "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={returnTo} replace />;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    login.mutate({ email, password });
  };

  const errorMessage =
    login.error?.message || login.error?.error || "An error occurred";

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold uppercase tracking-[0.2em] mb-2">Cobar</h1>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Sign in to your account</p>
        </div>

        <div className="bg-[#191919] border border-[#616161]/20 rounded-lg p-8">
          {login.isError && (
            <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-sm mb-6">
              {errorMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                autoComplete="email"
                required
                placeholder="you@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>

            <div className="flex items-center justify-between">
              <Link
                to="/forgot-password"
                className="text-xs font-medium uppercase tracking-wider text-primary hover:underline"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={login.isPending}
            >
              {login.isPending ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </div>

        <p className="text-center text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Don't have an account?{" "}
          <Link
            to="/signup"
            state={location.state}
            className="text-primary hover:underline"
          >
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}
